import {
  X,
  Heart,
  MessageCircle,
  UserPlus,
  Star,
  Gift,
  Zap,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { supabase } from "../lib/supabase.js";
import { notificationService } from "../lib/notification-service";
import { toast } from "react-hot-toast";
import type { Notification } from "../types/index";

interface NotificationsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function NotificationsModal({
  isOpen,
  onClose,
}: NotificationsModalProps) {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);

  // Function to map notification types to icons
  const getNotificationIcon = (type: string) => {
    const iconMap: Record<string, any> = {
      like_post: Heart,
      comment_post: MessageCircle,
      follow_user: UserPlus,
      mention_user: MessageCircle,
      like_comment: Heart,
      reply_comment: MessageCircle,
      new_message: MessageCircle,
      event_invite: Star,
      product_update: Gift,
      system_message: Zap,
    };
    return iconMap[type] || MessageCircle;
  };

  // Function to format notification text
  const getNotificationText = (notification: any) => {
    const textMap: Record<string, string> = {
      like_post: "liked your post",
      comment_post: "commented on your post",
      follow_user: "started following you",
      mention_user: "mentioned you",
      like_comment: "liked your comment",
      reply_comment: "replied to your comment",
      new_message: "sent you a message",
      event_invite: "invited you to an event",
      product_update: "updated a product",
      system_message: "System notification",
    };
    return notification.content_preview || textMap[notification.type] || "interacted with your content";
  };

  // Load notifications when component mounts or when modal opens
  useEffect(() => {
    if (isOpen) {
      const fetchNotifications = async () => {
        try {
          setLoading(true);
          const notificationsData = await notificationService.getUserNotifications(20, 0);
          setNotifications(notificationsData);
          
          // Count unread notifications
          const unreadNotifications = notificationsData.filter((n: any) => !n.is_read);
          setUnreadCount(unreadNotifications.length);
        } catch (error) {
          console.error('Error fetching notifications:', error);
          toast.error('Failed to load notifications');
        } finally {
          setLoading(false);
        }
      };
      
      fetchNotifications();
    }
  }, [isOpen]);

  // Subscribe to real-time notifications
  useEffect(() => {
    // Get the current user
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) return;
      
      // Subscribe to changes in the notifications table for this user
      const subscription = supabase
        .channel('public:notifications')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications',
            filter: `recipient_user_id=eq.${user.id}`
          },
          (payload) => {
            // Handle new notification
            console.log('New notification:', payload.new);
            // Update notification state/count
            const newNotification = payload.new as Notification;
            setNotifications(prev => [newNotification, ...prev]);
            setUnreadCount(prev => prev + 1);
            // Show toast notification
            toast.success('New notification received');
          }
        )
        .subscribe();
      
      // Clean up subscription on unmount
      return () => {
        subscription.unsubscribe();
      };
    };
    
    getUser();
  }, []);

  if (!isOpen) return null;

  // Format notifications for display
  const formattedNotifications = notifications.map((notification) => {
    const Icon = getNotificationIcon(notification.type);
    return {
      id: notification.id,
      type: notification.type,
      icon: Icon,
      user: notification.sender?.username || 'User',
      verified: notification.sender?.verified || false,
      action: getNotificationText(notification),
      time: new Date(notification.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      unread: !notification.is_read,
    };
  });

  const handleNotificationsClick = () => {
    navigate("/notifications");
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50">
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />
      <div className="absolute bottom-0 left-0 w-64 bg-white dark:bg-black dark:shadow-[6px_-5px_6px_-1px_rgba(149,_157,_165,_0.2)] rounded-tr-xl h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
          <div>
            <button
              onClick={handleNotificationsClick}
              className="text-lg font-semibold dark:text-white hover:text-gray-600 dark:hover:text-gray-300"
            >
              Notifications
            </button>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {unreadCount} new {unreadCount === 1 ? 'notification' : 'notifications'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto scrollbar-hide">
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900 dark:border-white"></div>
            </div>
          ) : formattedNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-gray-500 dark:text-gray-400">
              <p>No notifications yet</p>
            </div>
          ) : (
            <div className="h-full">
              {formattedNotifications.map((notification) => {
                const Icon = notification.icon;
                return (
                  <div
                    key={notification.id}
                    onClick={handleNotificationsClick}
                    className={`flex items-start space-x-3 p-4 border-b border-gray-100 dark:border-gray-800 cursor-pointer ${
                      notification.unread ? "bg-gray-50 dark:bg-gray-800/50" : ""
                    }`}
                  >
                    <div
                      className={`p-2 rounded-lg ${
                        notification.unread
                          ? "bg-gray-200 dark:bg-gray-700"
                          : "bg-gray-100 dark:bg-gray-800"
                      }`}
                    >
                      <Icon className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div>
                          <p
                            className={`text-sm ${
                              notification.unread
                                ? "text-gray-900 dark:text-white font-medium"
                                : "text-gray-600 dark:text-gray-400"
                            }`}
                          >
                            <div className="flex items-center">
                              <span className="font-semibold">{notification.user}</span>
                              {notification.verified && (
                                <svg className="w-3.5 h-3.5 ml-1 text-blue-500" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                                </svg>
                              )}
                            </div>{" "}
                            {notification.action}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {notification.time}
                          </p>
                        </div>
                        {notification.unread && (
                          <div className="w-2 h-2 bg-gray-900 dark:bg-gray-400 rounded-full mt-1"></div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
